import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import { sliderBottom } from "d3-simple-slider";
import Loading from "../../common/components/loading/loading";
import {
  appendSignedVerticalAreaGradients,
  appendTimeSeriesTooltipOverlay,
  buildTooltipHtml,
  createTooltipHandlers,
  createZeroAnchoredArea,
  dashboardSeriesColors,
  formatTooltipCurrency,
  formatTooltipDate,
  formatTooltipNumber,
  getNumericExtentIncludingZero,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const HourlyElectricityPricingAndLoadGraph = ({
  data,
  title,
  isLoading,
  initialStartDate,
  initialEndDate,
}) => {
  const svgRef = useRef();
  const sliderRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    if (!svg.node()) {
      return;
    }

    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight - 20;
    const margin = { top: 30, right: 60, bottom: 45, left: 35 };

    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%SZ");
    const priceColor = dashboardSeriesColors.accentRed;
    const loadColor = dashboardSeriesColors.accentBlue;

    const formattedData = data
      .filter(
        (d) =>
          d.beginDateTimeMpt &&
          !(d.poolPrice === null && d.albertaInternalLoad === null),
      )
      .map((d) => ({
        beginDateTimeMpt: parseDate(d.beginDateTimeMpt),
        poolPrice: d.poolPrice,
        albertaInternalLoad: d.albertaInternalLoad,
      }));

    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none")
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(formattedData, (d) => d.beginDateTimeMpt))
      .range([0, innerWidth]);

    const validPriceData = formattedData.filter(
      (d) => d.poolPrice != null && !isNaN(d.poolPrice),
    );
    const validLoadData = formattedData.filter(
      (d) => d.albertaInternalLoad != null && !isNaN(d.albertaInternalLoad),
    );

    const yScale = d3
      .scaleLinear()
      .domain(getNumericExtentIncludingZero(validPriceData.map((d) => d.poolPrice)))
      .nice()
      .range([innerHeight, 0]);

    const yScaleSecondary = d3
      .scaleLinear()
      .domain(
        getNumericExtentIncludingZero(
          validLoadData.map((d) => d.albertaInternalLoad),
        ),
      )
      .nice()
      .range([innerHeight, 0]);

    chartSvg
      .append("g")
      .attr("class", "x-axis d3-chart-axis")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale));

    const formatCurrency = d3.format("$,.0f");

    const yAxisPrimary = chartSvg
      .append("g")
      .attr("class", "y-axis-primary d3-chart-axis")
      .call(d3.axisLeft(yScale).tickFormat(formatCurrency));

    const yAxisSecondary = chartSvg
      .append("g")
      .attr("class", "y-axis-secondary d3-chart-axis")
      .attr("transform", `translate(${innerWidth},0)`)
      .call(d3.axisRight(yScaleSecondary));

    chartSvg
      .append("text")
      .attr("transform", "rotate(90)")
      .attr("x", innerHeight / 2)
      .attr("y", -(innerWidth + margin.right - 10))
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .text("Load (MW)")
      .attr("class", "d3-chart-axis-label");

    const gridlines = chartSvg
      .append("g")
      .attr("class", "grid d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    const linePoolPrice = d3
      .line()
      .defined((d) => d.poolPrice != null && !isNaN(d.poolPrice))
      .x((d) => xScale(d.beginDateTimeMpt))
      .y((d) => yScale(d.poolPrice))
      .curve(d3.curveMonotoneX);

    const lineInternalLoad = d3
      .line()
      .defined(
        (d) => d.albertaInternalLoad != null && !isNaN(d.albertaInternalLoad),
      )
      .x((d) => xScale(d.beginDateTimeMpt))
      .y((d) => yScaleSecondary(d.albertaInternalLoad))
      .curve(d3.curveMonotoneX);

    const priceAreas = {
      positive: createZeroAnchoredArea({
        valueAccessor: (d) => d.poolPrice,
        xAccessor: (d) => d.beginDateTimeMpt,
        xScale,
        yScale,
      }),
      negative: createZeroAnchoredArea({
        polarity: "negative",
        valueAccessor: (d) => d.poolPrice,
        xAccessor: (d) => d.beginDateTimeMpt,
        xScale,
        yScale,
      }),
    };

    const loadAreas = {
      positive: createZeroAnchoredArea({
        valueAccessor: (d) => d.albertaInternalLoad,
        xAccessor: (d) => d.beginDateTimeMpt,
        xScale,
        yScale: yScaleSecondary,
      }),
      negative: createZeroAnchoredArea({
        polarity: "negative",
        valueAccessor: (d) => d.albertaInternalLoad,
        xAccessor: (d) => d.beginDateTimeMpt,
        xScale,
        yScale: yScaleSecondary,
      }),
    };

    const priceGradients = appendSignedVerticalAreaGradients({
      chartSvg,
      idPrefix: "hourly-price-load-price",
      color: priceColor,
    });
    const loadGradients = appendSignedVerticalAreaGradients({
      chartSvg,
      idPrefix: "hourly-price-load-load",
      color: loadColor,
    });

    const areaLayer = chartSvg.append("g").attr("class", "dashboard-area-layer");
    const areaPaths = {
      priceNegative: areaLayer
        .append("path")
        .datum(formattedData)
        .attr("class", "d3-chart-area dashboard-area-highlight")
        .attr("fill", `url(#${priceGradients.negativeGradientId})`)
        .attr("d", priceAreas.negative),
      pricePositive: areaLayer
        .append("path")
        .datum(formattedData)
        .attr("class", "d3-chart-area dashboard-area-highlight")
        .attr("fill", `url(#${priceGradients.positiveGradientId})`)
        .attr("d", priceAreas.positive),
      loadNegative: areaLayer
        .append("path")
        .datum(formattedData)
        .attr("class", "d3-chart-area dashboard-area-highlight")
        .attr("fill", `url(#${loadGradients.negativeGradientId})`)
        .attr("d", loadAreas.negative),
      loadPositive: areaLayer
        .append("path")
        .datum(formattedData)
        .attr("class", "d3-chart-area dashboard-area-highlight")
        .attr("fill", `url(#${loadGradients.positiveGradientId})`)
        .attr("d", loadAreas.positive),
    };

    const lineLayer = chartSvg.append("g").attr("class", "dashboard-line-layer");

    const priceLinePath = lineLayer
      .append("path")
      .datum(formattedData)
      .attr("class", "price-line")
      .attr("fill", "none")
      .attr("stroke", priceColor)
      .attr("stroke-width", 2)
      .attr("d", linePoolPrice);

    const loadLinePath = lineLayer
      .append("path")
      .datum(formattedData)
      .attr("class", "load-line")
      .attr("fill", "none")
      .attr("stroke", loadColor)
      .attr("stroke-width", 2)
      .attr("d", lineInternalLoad);

    const legend = chartSvg
      .append("g")
      .attr("transform", `translate(${innerWidth / 2}, -10)`);

    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text(title)
      .attr("class", "d3-chart-title");

    legend
      .append("line")
      .attr("x1", innerWidth / 4 - 60)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 - 30)
      .attr("y2", 5)
      .attr("stroke", priceColor)
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", innerWidth / 4 - 25)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Price");

    legend
      .append("line")
      .attr("x1", innerWidth / 4 + 30)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 + 60)
      .attr("y2", 5)
      .attr("stroke", loadColor)
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", innerWidth / 4 + 65)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Load");

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });
    let currentTooltipData = formattedData;

    appendTimeSeriesTooltipOverlay({
      chartSvg,
      innerWidth,
      innerHeight,
      xScale,
      getData: () => currentTooltipData,
      xAccessor: (d) => d.beginDateTimeMpt,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) =>
        buildTooltipHtml({
          title: formatTooltipDate(d.beginDateTimeMpt, "%Y-%m-%d %H:%M"),
          rows: [
            {
              label: "Price",
              value: formatTooltipCurrency(d.poolPrice, 0),
              color: priceColor,
            },
            {
              label: "Load",
              value: `${formatTooltipNumber(d.albertaInternalLoad, {
                maximumFractionDigits: 0,
              })} MW`,
              color: loadColor,
            },
          ],
        }),
    });

    const sliderSvg = d3
      .select(sliderRef.current)
      .attr("width", parentWidth)
      .attr("height", 40)
      .attr("class", "no-print")
      .attr("transform", "translate(0,-20)");

    sliderSvg.selectAll("*").remove();

    const sliderWidth = innerWidth;

    const updateChart = (range) => {
      const [startDate, endDate] = range;

      const filteredData = formattedData.filter(
        (d) => d.beginDateTimeMpt >= startDate && d.beginDateTimeMpt <= endDate,
      );

      currentTooltipData = filteredData;

      const validFilteredPriceData = filteredData.filter(
        (d) => d.poolPrice != null && !isNaN(d.poolPrice),
      );
      const validFilteredLoadData = filteredData.filter(
        (d) => d.albertaInternalLoad != null && !isNaN(d.albertaInternalLoad),
      );

      xScale.domain([startDate, endDate]);
      yScale
        .domain(getNumericExtentIncludingZero(validFilteredPriceData.map((d) => d.poolPrice)))
        .nice();
      yScaleSecondary
        .domain(
          getNumericExtentIncludingZero(
            validFilteredLoadData.map((d) => d.albertaInternalLoad),
          ),
        )
        .nice();

      const t = d3.transition().duration(50).ease(d3.easeLinear);

      chartSvg.select(".x-axis").transition(t).call(d3.axisBottom(xScale));

      yAxisPrimary
        .transition(t)
        .call(d3.axisLeft(yScale).tickFormat(formatCurrency));

      yAxisSecondary
        .transition(t)
        .call(d3.axisRight(yScaleSecondary));

      gridlines
        .transition(t)
        .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

      areaPaths.priceNegative.datum(filteredData).transition(t).attr("d", priceAreas.negative);
      areaPaths.pricePositive.datum(filteredData).transition(t).attr("d", priceAreas.positive);
      areaPaths.loadNegative.datum(filteredData).transition(t).attr("d", loadAreas.negative);
      areaPaths.loadPositive.datum(filteredData).transition(t).attr("d", loadAreas.positive);

      priceLinePath.datum(filteredData).transition(t).attr("d", linePoolPrice);
      loadLinePath.datum(filteredData).transition(t).attr("d", lineInternalLoad);
    };

    if (!formattedData || formattedData.length === 0) {
      return;
    }

    let minDate = d3.min(formattedData, (d) => d.beginDateTimeMpt);
    let maxDate = d3.max(formattedData, (d) => d.beginDateTimeMpt);

    if (!minDate || !maxDate) {
      minDate = new Date();
      maxDate = new Date();
    }

    const throttledUpdateChart = throttle(updateChart, 30);

    const slider = sliderBottom()
      .min(minDate)
      .max(maxDate)
      .width(sliderWidth)
      .tickFormat(d3.timeFormat("%Y-%m-%d"))
      .ticks(5)
      .default([initialStartDate || minDate, initialEndDate || maxDate])
      .fill("var(--theme-interactive-element)")
      .on("onchange", throttledUpdateChart);

    sliderSvg
      .append("g")
      .attr("transform", `translate(${(parentWidth - sliderWidth) / 2},0)`)
      .call(slider);

    sliderSvg.selectAll(".slider text").style("fill", "grey");
    sliderSvg.selectAll(".tick text").style("opacity", "0");

    updateChart([
      initialStartDate || d3.min(formattedData, (d) => d.beginDateTimeMpt ?? new Date()),
      initialEndDate || d3.max(formattedData, (d) => d.beginDateTimeMpt ?? new Date()),
    ]);
  }, [data, title, initialStartDate, initialEndDate]);

  const throttle = (func, limit) => {
    let lastFunc;
    let lastRan;
    return function () {
      const context = this;
      const args = arguments;
      if (!lastRan) {
        func.apply(context, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(
          function () {
            if (Date.now() - lastRan >= limit) {
              func.apply(context, args);
              lastRan = Date.now();
            }
          },
          limit - (Date.now() - lastRan),
        );
      }
    };
  };

  useEffect(() => {
    !isLoading && renderChart();
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current;
    if (!svgNode) return;

    const throttledResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100);
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;
  return (
    <div className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}>
      <svg ref={svgRef} style={{ width: "100%" }}></svg>
      <svg ref={sliderRef} style={{ width: "100%" }}></svg>
    </div>
  );
};

export default HourlyElectricityPricingAndLoadGraph;







